import React, { Component } from "react";
import { Tabs, Tab, Button } from "react-bootstrap";
import Loading from "../../components/Loading";
import * as Http from "../../http";
import * as Business from "../../utils/business";

const MODULES = [
  { key: "RAROC", label: "RAROC" },
  { key: "CAR", label: "CAR" },
  { key: "ICAAP", label: "ICAAP" },
  { key: "PRICING", label: "Pricing" },
  { key: "CAPITAL_PLANNING", label: "Capital Planning" },
];

export default class ReportPermission extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingUsers: true,
      users: [],
      userSearch: "",
      selectedUserId: null,
      selectedUsername: "",
      selectedFullname: "",
      activeModule: "RAROC",
      loadingReports: false,
      availableReports: [],
      assignedReports: [],
      reportSearch: "",
      saving: false,
      saveSuccess: false,
      saveError: false,
    };
    this._toastTimer = null;
  }

  componentDidMount() {
    Http.getReportPermissionUsers().then((res) => {
      this.setState({
        loadingUsers: false,
        users: (res && res.Data) || [],
      });
    });
  }

  selectUser = (user) => {
    this.setState(
      {
        selectedUserId: user.UserId,
        selectedUsername: user.Username,
        selectedFullname: user.Fullname,
        saveSuccess: false,
      },
      () => {
        this.loadReportsForModule(this.state.activeModule);
      },
    );
  };

  loadReportsForModule = (module) => {
    const { selectedUserId } = this.state;
    if (!selectedUserId) return;
    this.setState({
      loadingReports: true,
      availableReports: [],
      assignedReports: [],
      saveSuccess: false,
    });
    Promise.all([
      Http.getAvailableReports(module),
      Http.getUserReportPermissions(selectedUserId, module),
    ]).then(([availRes, permRes]) => {
      const available = (availRes && availRes.Data) || [];
      const assigned = ((permRes && permRes.Data) || []).map(
        (p) => p.report_name,
      );
      this.setState({
        loadingReports: false,
        availableReports: available,
        assignedReports: assigned,
      });
    });
  };

  onModuleChange = (module) => {
    this.setState(
      { activeModule: module, reportSearch: "", saveSuccess: false },
      () => {
        this.loadReportsForModule(module);
      },
    );
  };

  toggleReport = (reportName) => {
    const { assignedReports } = this.state;
    if (assignedReports.includes(reportName)) {
      this.setState({
        assignedReports: assignedReports.filter((r) => r !== reportName),
        saveSuccess: false,
      });
    } else {
      this.setState({
        assignedReports: [...assignedReports, reportName],
        saveSuccess: false,
      });
    }
  };

  toggleAll = () => {
    const { availableReports, assignedReports, reportSearch } = this.state;
    const filtered = this.getFilteredReports();
    const allSelected = filtered.every((r) => assignedReports.includes(r));
    if (allSelected) {
      this.setState({
        assignedReports: assignedReports.filter((r) => !filtered.includes(r)),
        saveSuccess: false,
      });
    } else {
      const toAdd = filtered.filter((r) => !assignedReports.includes(r));
      this.setState({
        assignedReports: [...assignedReports, ...toAdd],
        saveSuccess: false,
      });
    }
  };

  getFilteredReports = () => {
    const { availableReports, reportSearch } = this.state;
    if (!reportSearch.trim()) return availableReports;
    return availableReports.filter((r) =>
      r.toLowerCase().includes(reportSearch.toLowerCase()),
    );
  };

  showToast = (success) => {
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this.setState({ saveSuccess: success, saveError: !success });
    this._toastTimer = setTimeout(() => {
      this.setState({ saveSuccess: false, saveError: false });
      this._toastTimer = null;
    }, 3000);
  };

  componentWillUnmount() {
    if (this._toastTimer) clearTimeout(this._toastTimer);
  }

  savePermissions = () => {
    const { selectedUserId, activeModule, assignedReports } = this.state;
    this.setState({ saving: true, saveSuccess: false, saveError: false });
    Http.setUserReportPermissions(
      selectedUserId,
      activeModule,
      assignedReports,
    ).then((res) => {
      this.setState({ saving: false });
      this.showToast(!!(res && res.Success));
    });
  };

  getFilteredUsers = () => {
    const { users, userSearch } = this.state;
    if (!userSearch.trim()) return users;
    const q = userSearch.toLowerCase();
    return users.filter(
      (u) =>
        (u.Username || "").toLowerCase().includes(q) ||
        (u.Fullname || "").toLowerCase().includes(q),
    );
  };

  render() {
    const {
      loadingUsers,
      userSearch,
      selectedUserId,
      selectedUsername,
      selectedFullname,
      activeModule,
      loadingReports,
      assignedReports,
      reportSearch,
      saving,
      saveSuccess,
      saveError,
    } = this.state;

    const filteredUsers = this.getFilteredUsers();
    const filteredReports = this.getFilteredReports();
    const allSelected =
      filteredReports.length > 0 &&
      filteredReports.every((r) => assignedReports.includes(r));

    return (
      <div className="card" style={{ margin: "20px" }}>
        {/* Toast notification */}
        {(saveSuccess || saveError) && (
          <div
            style={{
              position: "fixed",
              top: "24px",
              right: "24px",
              zIndex: 9999,
              padding: "12px 20px",
              borderRadius: "6px",
              background: saveSuccess ? "#4caf50" : "#f44336",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className={saveSuccess ? "pe-7s-check" : "pe-7s-close-circle"} />
            {saveSuccess
              ? "Saved successfully!"
              : "Save failed. Please try again."}
          </div>
        )}
        <div className="header">
          <h4 className="title">Report Access Control</h4>
          <p className="category">Assign report access to users per module</p>
        </div>
        <div className="content">
          <div style={{ display: "flex", gap: "24px", minHeight: "500px" }}>
            {/* Left Panel: User List */}
            <div
              style={{
                width: "280px",
                flexShrink: 0,
                borderRight: "1px solid #e0e0e0",
                paddingRight: "16px",
              }}
            >
              <div style={{ marginBottom: "12px" }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search user..."
                  value={userSearch}
                  style={{ height: "32px", fontSize: "13px" }}
                  onChange={(e) =>
                    this.setState({ userSearch: e.target.value })
                  }
                />
              </div>
              {loadingUsers ? (
                <Loading size="small" />
              ) : (
                <div style={{ maxHeight: "520px", overflowY: "auto" }}>
                  {filteredUsers.length === 0 ? (
                    <p style={{ color: "#999", fontSize: "13px" }}>
                      No users found
                    </p>
                  ) : (
                    filteredUsers.map((user) => (
                      <div
                        key={user.UserId}
                        onClick={() => this.selectUser(user)}
                        style={{
                          padding: "10px 12px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          marginBottom: "4px",
                          background:
                            selectedUserId === user.UserId
                              ? "#FFE600"
                              : "#f8f8f8",
                          borderLeft:
                            selectedUserId === user.UserId
                              ? "4px solid #333"
                              : "4px solid transparent",
                          transition: "all 0.15s",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: "#333",
                          }}
                        >
                          {user.Username}
                        </div>
                        {user.Fullname && (
                          <div style={{ fontSize: "11px", color: "#777" }}>
                            {user.Fullname}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Right Panel: Module Tabs + Reports */}
            <div style={{ flex: 1 }}>
              {!selectedUserId ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                    color: "#aaa",
                    fontSize: "15px",
                  }}
                >
                  <i
                    className="pe-7s-user"
                    style={{ fontSize: "40px", marginRight: "12px" }}
                  />
                  Select a user to manage report access
                </div>
              ) : (
                <div>
                  <div
                    style={{
                      marginBottom: "16px",
                      borderBottom: "2px solid #FFE600",
                      paddingBottom: "8px",
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: "15px" }}>
                      {selectedFullname || selectedUsername}
                    </span>
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "12px",
                        color: "#888",
                      }}
                    >
                      @{selectedUsername}
                    </span>
                  </div>
                  <Tabs
                    activeKey={activeModule}
                    onSelect={this.onModuleChange}
                    id="module-tabs"
                    style={{ marginBottom: "16px" }}
                  >
                    {MODULES.map((m) => (
                      <Tab key={m.key} eventKey={m.key} title={m.label} />
                    ))}
                  </Tabs>

                  {/* Search + Select All row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search report..."
                      value={reportSearch}
                      style={{
                        height: "30px",
                        fontSize: "13px",
                        width: "220px",
                      }}
                      onChange={(e) =>
                        this.setState({ reportSearch: e.target.value })
                      }
                    />
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: 0,
                        fontSize: "13px",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={this.toggleAll}
                        disabled={filteredReports.length === 0}
                      />
                      Select All
                    </label>
                    <div
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Button
                        bsSize="small"
                        bsStyle="primary"
                        style={{
                          background: "#FFE600",
                          borderColor: "#ccc",
                          color: "#333",
                          fontWeight: 600,
                        }}
                        disabled={saving}
                        onClick={this.savePermissions}
                      >
                        {saving ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </div>

                  {loadingReports ? (
                    <Loading size="small" />
                  ) : (
                    <div
                      style={{
                        maxHeight: "420px",
                        overflowY: "auto",
                        border: "1px solid #eee",
                        borderRadius: "6px",
                        padding: "8px",
                      }}
                    >
                      {filteredReports.length === 0 ? (
                        <p
                          style={{
                            color: "#aaa",
                            fontSize: "13px",
                            padding: "12px",
                          }}
                        >
                          No reports available for this module.
                        </p>
                      ) : (
                        filteredReports.map((reportName) => (
                          <label
                            key={reportName}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "8px 12px",
                              borderRadius: "5px",
                              cursor: "pointer",
                              marginBottom: "2px",
                              background: assignedReports.includes(reportName)
                                ? "#fffde7"
                                : "transparent",
                              borderLeft: assignedReports.includes(reportName)
                                ? "3px solid #FFE600"
                                : "3px solid transparent",
                              transition: "background 0.1s",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={assignedReports.includes(reportName)}
                              onChange={() => this.toggleReport(reportName)}
                            />
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: assignedReports.includes(reportName)
                                  ? 600
                                  : 400,
                              }}
                            >
                              {reportName}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// src/pages/ReportPermission/ReportPermission.js